import { useState } from 'react';
import { X, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Asset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
}

interface AssetIssueItem {
  id: string;
  assetId: string;
  asset: Asset;
  condition: string;
  accessories?: string;
  notes?: string;
  issuedAt?: string;
}

interface ReturnAssetModalProps {
  open: boolean;
  onClose: () => void;
  item: AssetIssueItem | null;
  onReturn: (data: { returnCondition: string; notes?: string }) => void;
  isLoading?: boolean;
}

const CONDITION_OPTIONS = [
  { value: 'EXCELLENT', label: 'Excellent', description: 'Like new condition' },
  { value: 'GOOD', label: 'Good', description: 'Minor wear, fully functional' },
  { value: 'FAIR', label: 'Fair', description: 'Some wear, works well' },
  { value: 'POOR', label: 'Poor', description: 'Significant wear' },
  { value: 'DAMAGED', label: 'Damaged', description: 'Needs repair' },
];

export default function ReturnAssetModal({ open, onClose, item, onReturn, isLoading }: ReturnAssetModalProps) {
  const [returnCondition, setReturnCondition] = useState('GOOD');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!returnCondition) {
      setError('Please select the return condition');
      return;
    }

    onReturn({
      returnCondition,
      notes: notes.trim() || undefined,
    });
  };

  const resetForm = () => {
    setReturnCondition('GOOD');
    setNotes('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5" />
                Return Asset
              </CardTitle>
              <CardDescription>
                Record the return of this asset
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Asset Info */}
            <div className="p-3 rounded-lg bg-muted/50 mb-6">
              <p className="font-medium">{item.asset.name}</p>
              <p className="text-sm text-muted-foreground">
                <span className="font-mono">{item.asset.assetTag}</span>
                {item.asset.serialNumber && <span> · S/N: {item.asset.serialNumber}</span>}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Issued Condition: <span className="font-medium">{item.condition}</span>
              </p>
              {item.accessories && (
                <p className="text-sm text-muted-foreground">
                  Accessories: {item.accessories}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Return Condition */}
              <div className="space-y-2">
                <Label>
                  Return Condition <span className="text-destructive">*</span>
                </Label>
                <div className="grid gap-2">
                  {CONDITION_OPTIONS.map((condition) => (
                    <button
                      key={condition.value}
                      type="button"
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all text-left",
                        returnCondition === condition.value
                          ? "border-primary bg-primary/5"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => setReturnCondition(condition.value)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{condition.label}</p>
                          <p className="text-xs text-muted-foreground">{condition.description}</p>
                        </div>
                        {returnCondition === condition.value && (
                          <div className="w-4 h-4 rounded-full bg-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Any notes about the condition, missing accessories, damage, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Warning for damaged */}
              {returnCondition === 'DAMAGED' && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Asset Marked as Damaged</p>
                    <p className="text-xs text-yellow-700">
                      This asset will be flagged for repair or replacement. Please provide detailed notes about the damage.
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Processing...' : 'Confirm Return'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
